import type { ApolloCache } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  type CashFlowStatementQuery,
  type ChartData,
  type ReportsQuery,
  type ViewLayoutFragment,
  FrequencyEnum,
  MetricModeEnum,
} from "@/__generated__/graphql";
import { useAppRouteState } from "@/app/app/_/routing";
import {
  addReportGroupToDefinition,
  definitionForActiveReportGroup,
  definitionToProfile,
  getReportGroups,
  mergeCashflowCharts,
  normalizeReportDefinition,
  removeReportGroupFromDefinition,
  renameReportGroupInDefinition,
  reorderReportGroupsInDefinition,
  resolveActiveReportGroupId,
  type CashflowAggregationMode,
  type CashflowChartInput,
  reportLayoutDefinitionToGraphQLInput,
  type ReportLayoutDefinition,
  getReportSubnavFilters,
  getSelectedViewId,
} from "@/app/app/_/views/ReportsView/ReportsView.utils";
import { Mutations } from "@/constants/mutations";
import { Queries } from "@/constants/queries";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { useScope } from "@/hooks/useScope";
import { State } from "@/state";
import type { BreakpointKey, DashboardProfile } from "@/state/@types";
import {
  reportsGridModeFromWidth,
  type ReportsGridMode,
} from "@/app/app/_/views/ReportsView/reportsGridLayout";

// ── useReportsGridMode ──────────────────────────────────────────────────────

export function useReportsGridMode(
  containerEl: HTMLElement | null,
): ReportsGridMode {
  const [mode, setMode] = useState<ReportsGridMode>("grid");

  useEffect(() => {
    if (!containerEl) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setMode(reportsGridModeFromWidth(entry.contentRect.width));
      }
    });

    setMode(reportsGridModeFromWidth(containerEl.clientWidth));
    observer.observe(containerEl);

    return () => observer.disconnect();
  }, [containerEl]);

  return mode;
}

// ── useReportsViewBreakpoint ────────────────────────────────────────────────

function reportsBreakpointThresholds(): {
  key: BreakpointKey;
  minWidth: number;
}[] {
  return [
    { key: "xl", minWidth: 1400 },
    { key: "lg", minWidth: 1000 },
    { key: "md", minWidth: 720 },
    { key: "sm", minWidth: 400 },
    { key: "xs", minWidth: 0 },
  ];
}

function widthToBreakpoint(width: number): BreakpointKey {
  for (const bp of reportsBreakpointThresholds()) {
    if (width >= bp.minWidth) return bp.key;
  }
  return "xs";
}

export function useReportsViewBreakpoint(
  containerEl: HTMLElement | null,
): BreakpointKey {
  const [breakpoint, setBreakpoint] = useState<BreakpointKey>("md");

  useEffect(() => {
    if (!containerEl) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setBreakpoint(widthToBreakpoint(entry.contentRect.width));
      }
    });

    setBreakpoint(widthToBreakpoint(containerEl.clientWidth));
    observer.observe(containerEl);

    return () => observer.disconnect();
  }, [containerEl]);

  return breakpoint;
}

// ── useReportsViewCashflowChartData ────────────────────────────────────────────────────

type UseCashflowChartDataArgs = {
  entityIds: string[];
  entityNamesById: Map<string, string>;
  startDate?: string | null;
  endDate?: string | null;
  frequency?: FrequencyEnum | null;
  mode?: MetricModeEnum | null;
  aggregationMode: CashflowAggregationMode;
};

export function useReportsViewCashflowChartData({
  entityIds,
  entityNamesById,
  startDate,
  endDate,
  frequency,
  mode,
  aggregationMode,
}: UseCashflowChartDataArgs) {
  const client = useApolloClient();
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(false);

  const entityKey = entityIds.join(",");

  useEffect(() => {
    if (!entityIds.length) {
      setChartData(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void (async () => {
      try {
        const results = await Promise.all(
          entityIds.map(async (entityIdentifier) => {
            const response = await client.query<CashFlowStatementQuery>({
              query: Queries.CASHFLOW_STATEMENT_QUERY,
              variables: {
                entityIdentifier,
                startDate: startDate ?? undefined,
                endDate: endDate ?? undefined,
                frequency: frequency ?? FrequencyEnum.Monthly,
                mode: mode ?? MetricModeEnum.Period,
              },
              fetchPolicy: "cache-first",
            });

            const chart =
              response.data?.statements?.cashFlowStatement?.data
                ?.totalCashFlowsChart ?? null;

            return {
              entityId: entityIdentifier,
              entityName:
                entityNamesById.get(entityIdentifier) ?? entityIdentifier,
              chart,
            };
          }),
        );

        if (cancelled) return;

        const inputs = results.filter(
          (item): item is CashflowChartInput & { chart: ChartData } =>
            item.chart != null,
        );

        setChartData(mergeCashflowCharts(inputs, aggregationMode));
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [aggregationMode, client, endDate, entityKey, frequency, mode, startDate]);

  return { chartData, loading };
}

// ── useReportsViewReportLayouts ──────────────────────────────────────────────────────

type ReportLayoutRecord = {
  id?: string;
  identifier: string;
  name: string;
  /** The businesses it reads that its reader is in, in the order it reads them. */
  entities: { id: string; identifier: string; name: string }[];
  /** The business the report belongs to. */
  entity?: { id: string; identifier: string; name: string } | null;
  definition: ReportLayoutDefinition;
  linkSharingEnabled?: boolean;
  shareToken?: string | null;
};

function viewFromNodeMutationData(
  data: unknown,
  mutation: "createNode" | "updateNode",
): ViewLayoutFragment | undefined {
  const payload = (
    data as Record<string, { data?: ViewLayoutFragment[] }> | undefined
  )?.[mutation];
  return (
    payload?.data?.find((node) => node?.__typename === "ReportObject") ??
    payload?.data?.[0]
  );
}

function layoutToProfile(layout: ReportLayoutRecord): DashboardProfile {
  const def = normalizeReportDefinition(layout.definition);
  return definitionToProfile(layout.identifier, layout.name, def);
}

function patchReportLayoutDefinitionInCache(
  cache: ApolloCache,
  layoutIdentifier: string,
  definition: unknown,
) {
  cache.updateQuery<ReportsQuery>(
    { query: Queries.REPORTS_QUERY },
    (existing) => {
      if (!existing?.reports?.data) return existing;
      const layouts = existing.reports.data.map((layout) =>
        layout.identifier === layoutIdentifier
          ? {
              ...layout,
              definition:
                definition as ReportsQuery["reports"]["data"][number]["definition"],
            }
          : layout,
      );
      return { ...existing, reports: { ...existing.reports, data: layouts } };
    },
  );
}

export function useReportsViewReportLayouts() {
  const client = useApolloClient();
  const { activeEntityIdentifiers } = useScope();
  const [activeLayoutId, setActiveLayoutId] = useState<string | null>(null);
  const activeReportTabIds = State((s) => s.activeReportTabIds);
  const setActiveReportTabIdStore = State((s) => s.setActiveReportTabId);
  const routeState = useAppRouteState();
  const activeTab = State((s) => s.activeTab);
  const activeTabNodeIds = State((s) => s.activeTabNodeIds);
  const routeViewId = getSelectedViewId(
    activeTabNodeIds,
    activeTab,
    routeState.viewId,
  );

  // Every report of an entity the user is in - whichever are picked - as a
  // report is its entity's members'.
  const { data, loading, refetch } = useBackendQuery(Queries.REPORTS_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const layouts = useMemo(
    () => (data?.reports?.data ?? []) as ReportLayoutRecord[],
    [data],
  );

  const activeLayout = useMemo(() => {
    if (!layouts.length) return null;

    if (routeViewId) {
      const fromRoute = layouts.find(
        (layout) =>
          layout.id === routeViewId || layout.identifier === routeViewId,
      );
      if (fromRoute) return fromRoute;
    }

    const id = activeLayoutId ?? layouts[0]?.identifier;
    return (
      layouts.find((layout) => layout.identifier === id || layout.id === id) ??
      layouts[0]
    );
  }, [layouts, activeLayoutId, routeViewId]);

  useEffect(() => {
    if (!routeViewId || !layouts.length) return;
    const layout = layouts.find(
      (entry) => entry.id === routeViewId || entry.identifier === routeViewId,
    );
    if (layout && layout.identifier !== activeLayoutId) {
      setActiveLayoutId(layout.identifier);
    }
  }, [routeViewId, layouts, activeLayoutId]);

  const resolvedActiveLayoutId = activeLayout?.identifier ?? null;

  const profiles = useMemo(() => layouts.map(layoutToProfile), [layouts]);

  const normalizedDefinition = activeLayout
    ? normalizeReportDefinition(activeLayout.definition)
    : null;

  const reportTabs = normalizedDefinition
    ? getReportGroups(normalizedDefinition)
    : [];

  const activeReportTabId =
    resolvedActiveLayoutId && normalizedDefinition
      ? resolveActiveReportGroupId(
          normalizedDefinition,
          activeReportTabIds[resolvedActiveLayoutId],
        )
      : null;

  const activeDefinition = useMemo(
    () =>
      normalizedDefinition
        ? definitionForActiveReportGroup(
            normalizedDefinition,
            activeReportTabId,
          )
        : null,
    [normalizedDefinition, activeReportTabId],
  );

  const activeProfile = activeLayout ? layoutToProfile(activeLayout) : null;

  const isReportPublic = Boolean(activeLayout?.linkSharingEnabled);

  const setActiveReportTabId = useCallback(
    (layoutIdentifier: string, tabId: string) => {
      setActiveReportTabIdStore(layoutIdentifier, tabId);
    },
    [setActiveReportTabIdStore],
  );

  const { mutate: updateLayout } = useBackendMutation({
    mutation: Mutations.UPDATE_OVERVIEW_LAYOUT_MUTATION,
  });
  const layoutIdForIdentifier = useCallback(
    (identifier: string) =>
      layouts.find((l) => l.identifier === identifier)?.id,
    [layouts],
  );

  const buildUpdateLayoutVariables = useCallback(
    (id: string, report: Record<string, unknown>) => {
      const payload = { ...report };
      if (payload.definition != null) {
        payload.definition = reportLayoutDefinitionToGraphQLInput(
          payload.definition as ReportLayoutDefinition,
        );
      }
      return {
        input: {
          id,
          entityIdentifiers: activeEntityIdentifiers,
          node: { report: payload },
        },
      };
    },
    [activeEntityIdentifiers],
  );

  const saveDefinition = useCallback(
    async (identifier: string, definition: unknown) => {
      const id = layoutIdForIdentifier(identifier);
      if (!id) return;
      await updateLayout({
        variables: buildUpdateLayoutVariables(id, { definition }),
        update: (cache, { data }) => {
          const view = viewFromNodeMutationData(data, "updateNode");
          if (!view?.definition) return;
          patchReportLayoutDefinitionInCache(
            cache,
            identifier,
            view.definition,
          );
        },
      });
    },
    [
      activeEntityIdentifiers,
      buildUpdateLayoutVariables,
      layoutIdForIdentifier,
      updateLayout,
    ],
  );

  const addReportTab = useCallback(async () => {
    if (!resolvedActiveLayoutId || !normalizedDefinition) return;
    const { definition: nextDefinition, newGroupId: newTabId } =
      addReportGroupToDefinition(normalizedDefinition);
    await saveDefinition(resolvedActiveLayoutId, nextDefinition);
    setActiveReportTabId(resolvedActiveLayoutId, newTabId);
    await refetch();
  }, [
    resolvedActiveLayoutId,
    normalizedDefinition,
    refetch,
    saveDefinition,
    setActiveReportTabId,
  ]);

  const reorderReportTabs = useCallback(
    async (orderedTabIds: string[]) => {
      if (!resolvedActiveLayoutId || !normalizedDefinition) return;
      const nextDefinition = reorderReportGroupsInDefinition(
        normalizedDefinition,
        orderedTabIds,
      );
      if (nextDefinition === normalizedDefinition) return;
      await saveDefinition(resolvedActiveLayoutId, nextDefinition);
      await refetch();
    },
    [resolvedActiveLayoutId, normalizedDefinition, refetch, saveDefinition],
  );

  const renameReportTab = useCallback(
    async (tabId: string, name: string) => {
      if (!resolvedActiveLayoutId || !normalizedDefinition) return;
      const nextDefinition = renameReportGroupInDefinition(
        normalizedDefinition,
        tabId,
        name,
      );
      if (nextDefinition === normalizedDefinition) return;
      await saveDefinition(resolvedActiveLayoutId, nextDefinition);
      await refetch();
    },
    [resolvedActiveLayoutId, normalizedDefinition, refetch, saveDefinition],
  );

  const closeReportTab = useCallback(
    async (tabId: string) => {
      if (!resolvedActiveLayoutId || !normalizedDefinition) return;
      const groups = getReportGroups(normalizedDefinition);
      const closedIndex = groups.findIndex((g) => g.id === tabId);
      const nextDefinition = removeReportGroupFromDefinition(
        normalizedDefinition,
        tabId,
      );
      if (!nextDefinition) return;

      const isClosingActive = tabId === activeReportTabId;
      const remaining = getReportGroups(nextDefinition);

      await saveDefinition(resolvedActiveLayoutId, nextDefinition);

      if (isClosingActive && remaining.length > 0) {
        const nextIndex = Math.min(
          closedIndex >= 0 ? closedIndex : 0,
          remaining.length - 1,
        );
        setActiveReportTabId(resolvedActiveLayoutId, remaining[nextIndex]!.id);
      }

      await refetch();
    },
    [
      resolvedActiveLayoutId,
      activeReportTabId,
      normalizedDefinition,
      refetch,
      saveDefinition,
      setActiveReportTabId,
    ],
  );

  const renameLayout = useCallback(
    async (identifier: string, name: string) => {
      const id = layoutIdForIdentifier(identifier);
      if (!id) return;
      await updateLayout({
        variables: buildUpdateLayoutVariables(id, { name }),
      });
      await refetch();
    },
    [buildUpdateLayoutVariables, layoutIdForIdentifier, updateLayout, refetch],
  );

  const enableLinkSharing = useCallback(
    async (identifier: string) => {
      const id = layoutIdForIdentifier(identifier);
      if (!id) return undefined;
      const result = await updateLayout({
        variables: buildUpdateLayoutVariables(id, {
          enableLinkSharing: true,
        }),
      });
      await refetch();
      return (
        viewFromNodeMutationData(result?.data, "updateNode")?.shareToken ??
        undefined
      );
    },
    [buildUpdateLayoutVariables, layoutIdForIdentifier, updateLayout, refetch],
  );

  const disableLinkSharing = useCallback(
    async (identifier: string) => {
      const id = layoutIdForIdentifier(identifier);
      if (!id) return;
      await updateLayout({
        variables: buildUpdateLayoutVariables(id, {
          disableLinkSharing: true,
        }),
      });
      await refetch();
    },
    [buildUpdateLayoutVariables, layoutIdForIdentifier, updateLayout, refetch],
  );

  const setReportPublic = useCallback(
    async (identifier: string, isPublic: boolean) => {
      if (isPublic) {
        await enableLinkSharing(identifier);
        return;
      }
      await disableLinkSharing(identifier);
    },
    [disableLinkSharing, enableLinkSharing],
  );

  const updateShareEmails = useCallback(
    async (identifier: string, emails: string[]) => {
      const id = layoutIdForIdentifier(identifier);
      if (!id) return;
      await updateLayout({
        variables: buildUpdateLayoutVariables(id, { shareEmails: emails }),
      });
    },
    [buildUpdateLayoutVariables, layoutIdForIdentifier, updateLayout],
  );

  return {
    layouts,
    profiles,
    activeLayout,
    activeDefinition,
    normalizedDefinition,
    activeProfile,
    activeLayoutId: resolvedActiveLayoutId,
    setActiveLayoutId,
    reportTabs,
    activeReportTabId,
    setActiveReportTabId,
    addReportTab,
    closeReportTab,
    renameReportTab,
    reorderReportTabs,
    loading,
    refetch,
    saveDefinition,
    renameLayout,
    enableLinkSharing,
    disableLinkSharing,
    setReportPublic,
    isReportPublic,
    updateShareEmails,
  };
}

// ── Filter setup ────────────────────────────────────────────────────────

/**
 * Seed report subnav filter values (dates, frequency, entities).
 *
 * The entities filter starts as every business the report reads. Told to read
 * others, the report starts it again from all of them: kept, a narrowing picked
 * from the old ones left a business just added out of every widget. A report
 * opened keeps the filter it is opened with, as the URL says it.
 */
export function useReportsViewReportSubnavFilterSetup(
  reportIdentifier: string | undefined,
  entityIdentifiers: string[],
) {
  const entityKey = entityIdentifiers.join("|");
  const seededRef = useRef<{
    reportIdentifier: string | undefined;
    entityKey: string;
  } | null>(null);

  useEffect(() => {
    const isReadingOthers =
      seededRef.current?.reportIdentifier === reportIdentifier &&
      seededRef.current?.entityKey !== entityKey;
    seededRef.current = { reportIdentifier, entityKey };
    const currentState = State.getState();
    // Definitions are passed locally by ReportsViewFiltersSubnav — do not merge
    // them into global filterDefinitions or connection queries pick them up.
    State.setState({
      filters: getReportSubnavFilters(
        isReadingOthers
          ? currentState.filters.filter((filter) => filter.key !== "entities")
          : currentState.filters,
        entityIdentifiers,
      ),
    });
  }, [reportIdentifier, entityKey]);
}
